import { useState, useEffect, useRef, useMemo } from "react";
import { PackageSchema } from "../../../../utils/types";
import { Search, Check, Coffee } from 'react-feather';
import Textbox from "../../../../components/textbox/Textbox";
import API from "../../../../utils/api";
import { sleep } from "../../../../utils/sleep";

import "./PackagesSearch.scss";
import PackageCard from "../../../packages/PackageCard";
import { ContentButtonGroup } from "../../../../components/button/ContentButton";
import { ContentButton } from "../../../../components/button/ContentButton";
import { useLocation, useSearchParams } from "react-router-dom";

interface Props {
  botPackages: {[key: string]: any};
  onPackagesUpdated: ({ packages, addedPackages, removedPackages, modifiedPackages }: { packages: {[key: string]: any}, addedPackages: string[], removedPackages: string[], modifiedPackages: string[] }) => void;
};

export function PackagesSearch({
  botPackages,
  onPackagesUpdated
}: Props) {

  const [ searchParams ] = useSearchParams();
  const location = useLocation();
  const view = searchParams.get('view') || 'installed';
  const urls = {
    'installed': (() => {
      const url = new URL(location.pathname + location.search, 'https://not-a-real-url.com');
      url.searchParams.delete('view');
      return url.pathname + url.search;
    })(),
    'discover': (() => {
      const url = new URL(location.pathname + location.search, 'https://not-a-real-url.com');
      url.searchParams.set('view', 'discover');
      return url.pathname + url.search;
    })(),
    'development': (() => {
      const url = new URL(location.pathname + location.search, 'https://not-a-real-url.com');
      url.searchParams.set('view', 'development');
      return url.pathname + url.search;
    })()
  };

  const [isLoading, setIsLoading] = useState(true); // used for initial load
  const [isSearching, setIsSearching] = useState(false);
  const [packageSearch, setPackageSearch] = useState("");
  const [packages, setPackages] = useState<PackageSchema[]>([]);
  const [installedPackages, setInstalledPackages] = useState<PackageSchema[]>([]);
  const [developmentPackages, setDevelopmentPackages] = useState<PackageSchema[]>([]);
  const packagesRef = useRef(0);

  // Must do this...
  const installedPackagesList = useMemo(() => {
    return Object.keys(botPackages).map(key => `${key}/${botPackages[key].version}`);
  }, [botPackages]);

  const pageView = {
    'installed': 'installed',
    'discover': 'discover',
    'development': 'development'
  }[view] || view;

  const fetchPackages = async ({ delay = 300 }: { delay?: number }) => {
    setIsSearching(true);
    const id = ++packagesRef.current;
    await sleep(delay);
    if (id !== packagesRef.current) {
      return;
    }
    const [
      installedPackagesResponse,
      allPackagesResponse,
      developmentPackagesResponse
     ] = await Promise.all([
      API.get(
        'v1/packages',
        {
          show_private: true,
          show_environments: ['development', 'staging', 'production'],
          include: installedPackagesList,
        }
      ),
      API.get(
        'v1/packages',
        {
          show_private: true,
          is_approved: true,
          show_environments: ['production'],
          query: packageSearch,
          limit: { count: 3, offset: 0 }
        }
      ),
      API.get(
        'v1/packages',
        {
          show_private: true,
          is_approved: false,
          show_environments: ['development', 'staging', 'production'],
          query: packageSearch,
          limit: { count: 3, offset: 0 }
        }
      )
    ]);
    if (id !== packagesRef.current) {
      return;
    }
    const installedPackages = (installedPackagesResponse?.data || []) as Array<PackageSchema>;
    const packages = (allPackagesResponse?.data || []) as Array<PackageSchema>;
    const developmentPackages = (developmentPackagesResponse?.data || []) as Array<PackageSchema>;
    setIsSearching(false);
    setIsLoading(false);
    setInstalledPackages(installedPackages);
    setDevelopmentPackages(developmentPackages);
    setPackages(packages);
  };

  useEffect(() => {
    fetchPackages({ delay: 0 });
  }, [installedPackagesList]);

  const updatePackages = async (pkgs: {[key: string]: string}) => {
    const addedPackages = Object.keys(pkgs).filter(key => !botPackages[key]); // new packages
    const removedPackages = Object.keys(botPackages).filter(key => !pkgs[key]); // removed packages
    const modifiedPackages = Object.keys(pkgs)
      .filter(key => {
        return (
          !addedPackages.includes(key) &&
          !removedPackages.includes(key) &&
          JSON.stringify(botPackages[key]) !== JSON.stringify(pkgs[key])
        );
      }); // modified packages
    onPackagesUpdated({ packages: pkgs, addedPackages, removedPackages, modifiedPackages });
  };

  const viewPackages = pageView === 'installed'
    ? installedPackages
    : pageView === 'development'
      ? developmentPackages
      : packages;

  return (
    <div data-component="PackagesSearch">
      {false && (
        <div className="form-row">
          <Textbox
            heading="Search for packages..."
            endIcon={Search}
            value={packageSearch}
            loading={isSearching}
            onChange={search => {
              setPackageSearch(search);
              fetchPackages({});
            }}
          />
        </div>
      )}
      <div className="packages-actions">
        <div className="packages-actions-row">
          <ContentButtonGroup>
            <ContentButton
              icon={Check}
              active={pageView === 'installed'}
              color="grey"
              to={urls['installed']}
            >
              Installed {installedPackages.length ? `(${installedPackages.length})` : ''}
            </ContentButton>
            <ContentButton
              icon={Search}
              active={pageView === 'discover'}
              color="grey"
              to={urls['discover']}
            >
              Discover
            </ContentButton>
            <ContentButton
              icon={Coffee}
              active={pageView === 'development'}
              color="grey"
              to={urls['development']}
            >
              Development
            </ContentButton>
          </ContentButtonGroup>
        </div>
      </div>
      {pageView === 'installed' && (
        <>
          {installedPackages.length === 0 && !isLoading && (
            <div className="packages-results-empty">
              You have not installed any packages yet.<br /><br />
              <ContentButton
                icon={Search}
                color="blue"
                to={urls['discover']}
              >
                Discover packages
              </ContentButton>
            </div>
          )}
        </>
      )}
      {pageView === 'discover' && (
        <>
          {viewPackages.length === 0 && !isLoading && (
            <div className="packages-results-empty">
              No packages found.
            </div>
          )}
        </>
      )}
      {viewPackages.length > 0 && (
        <div className="packages-results">
          {viewPackages.map(pkg => {
            const installVersion = botPackages[pkg.display_name]?.version || '';
            let environment: string | undefined = void 0;
            let version = null;
            if (installVersion.startsWith('v-')) {
              environment = 'production';
              version = installVersion;
            } else if (installVersion) {
              environment = installVersion;
            }
            return (
              <PackageCard
                key={`${pageView}:${pkg.display_name}`}
                pkg={pkg}
                mode="install"
                environment={environment}
                version={version}
                installedPackages={botPackages}
                onInstall={(pkg, version, keys, permissions, sha256) => {
                  if (version) {
                    updatePackages({
                      ...botPackages,
                      [pkg.display_name]: {
                        version: version.version || version.environment || '',
                        permissions,
                        keys,
                        sha256
                      }
                    });
                  }
                }}
                onUninstall={(pkg) => {
                  const pkgs = {...botPackages};
                  delete pkgs[pkg.display_name];
                  updatePackages(pkgs);
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}